import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import HeaderBar from "../components/HeaderBar";
import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";
import AdminLayoutWrapper from "../components/AdminLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { HelpCircle, Search, Save, ChevronDown, ChevronUp, ChevronLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminWrapper from "../components/AdminWrapper";

/**
 * Modules & Rights Management (ASPX -> React)
 * - Inventory Rights: Active Inventory DB
 * - Payroll Rights: Payroll DB (needs finYearId header)
 *
 * Node endpoints:
 *  GET  /api/rights/usertypes
 *  GET  /api/rights/users?module=&userTypeId=   (Payroll requires x-finyear-id header)
 *  GET  /api/rights/modules
 *  GET  /api/rights/submodules?module=
 *  GET  /api/rights/pages?module=&userId=&moduleId=  (Payroll requires x-finyear-id header)
 *  POST /api/rights/save
 */

const ModulesAndRightsManagement = () => {
  const BASE_URL = "https://nissi-india.com";

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ---- UI selections ----
  const [mode, setMode] = useState("usertype"); // "usertype" | "user"
  const [overwriteAll, setOverwriteAll] = useState(false);

  const [userTypes, setUserTypes] = useState([]);
  const [users, setUsers] = useState([]);

  const [modules, setModules] = useState([]);
  const [submodules, setSubmodules] = useState([]);

  const [selectedUserTypeId, setSelectedUserTypeId] = useState("Select");
  const [selectedUserId, setSelectedUserId] = useState("Select");

  const [selectedModule, setSelectedModule] = useState("Select"); // Inventory / Payroll etc.
  const [selectedSubmoduleId, setSelectedSubmoduleId] = useState("Select"); // moduleId from tbl_module_mas

  const [loadingMeta, setLoadingMeta] = useState(false);
  const [loadingPages, setLoadingPages] = useState(false);
  const [saving, setSaving] = useState(false);

  // ---- Rights rows ----
  const [rows, setRows] = useState([]);

  const [showFilters, setShowFilters] = useState(true);

  // ---- Table scroller width (keeps empty/loading message centred in the visible area) ----
  const tableScrollRef = useRef(null);
  const [tableViewWidth, setTableViewWidth] = useState(0);

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    const update = () => setTableViewWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Payroll needs finYearId header
  const finYearId = Number(localStorage.getItem("finYearId") || 0);

  const isPayroll = selectedModule === "Payroll";

  // -----------------------------
  // Column sets (Inventory vs Payroll)
  // -----------------------------
  const inventoryColumns = useMemo(
    () => [
      { key: "rights", label: "Rights" },
      { key: "add", label: "Add" },
      { key: "edit", label: "Edit" },
      { key: "del", label: "Delete" },
      { key: "print", label: "Print" },
      { key: "email", label: "Email" },
      { key: "sms", label: "SMS" },
      { key: "camp", label: "Camp" },
      { key: "backdateentry", label: "Allow Backdate Entry" },
      { key: "approve", label: "Approve" },
      { key: "dailyprogress", label: "DailyProgress" },
      { key: "mdapprove", label: "MD Approve" },
    ],
    []
  );

  const payrollColumns = useMemo(
    () => [
      { key: "rights", label: "Rights" },
      { key: "add", label: "Add/Edit" }, // ASPX ties add+edit
      { key: "del", label: "Delete" },
      { key: "print", label: "Print" },
      { key: "email", label: "Email" },
      { key: "sms", label: "SMS" },
      { key: "depVisible", label: "Department" },
      { key: "dailyAttendanceSpecial", label: "Daily Attendance" }, // shown only for Daily Attendance page
    ],
    []
  );

  const columns = isPayroll ? payrollColumns : inventoryColumns;

  const navigate = useNavigate();

  const handleBack = () => {
    if (saving) return;
    navigate(-1);
  };

  // -----------------------------
  // ASPX RowDataBound visibility rules (converted)
  // -----------------------------
  function isCheckboxVisible(row, key) {
    if (!row) return true;

    if (!isPayroll) {
      // Inventory grid rules (gv_Rights_RowDataBound)

      const moduleDesc = String(row.fld_moduledescription || "");
      const pageName = String(row.fld_pagename || "");

      // Hide many columns in some module descriptions
      // We interpret ASPX cell hides as permission hides.

      if (moduleDesc === "Inventory Masters") {
        if (pageName !== "Item Category") {
          // Hide: print, email, sms, approve, dailyprogress, mdapprove (and some others)
          if (["print", "email", "sms", "approve", "dailyprogress", "mdapprove"].includes(key)) return false;
        }
      }

      if (moduleDesc === "Inventory Transactions") {
        // Hide: add?, edit?, rights? - ASPX hides some cells:
        // They hide add/edit? + print? + dailyprogress/mdapprove etc.
        if (["add", "edit", "print", "dailyprogress", "mdapprove"].includes(key)) return false;
      }

      if (moduleDesc === "Inventory Reports" || moduleDesc === "Vehicle Reports") {
        // Hide many action columns
        if (["add", "edit", "del", "camp", "approve", "dailyprogress", "mdapprove"].includes(key)) return false;
      }

      // Page-specific rules (Store Issue Note, GRN Confirmation Entry, Bill Passing, STN..., Tender Details, Daily Progress Entry)
      // These control approve/dailyprogress/mdapprove visibility
      // We replicate intent:
      if (pageName === "Store Issue Note") {
        // show approve + mdapprove? (ASPX shows 14 & 15 and hides 16 & 17)
        if (key === "approve" || key === "mdapprove") return true;
        if (key === "dailyprogress") return false;
      } else if (["Grn Confirmation Entry", "Bill Passing", "STN Inward/Outward", "Direct STN Outward"].includes(pageName)) {
        // show mdapprove only
        if (key === "approve") return false;
        if (key === "mdapprove") return true;
        if (key === "dailyprogress") return false;
      } else if (pageName === "Tender Details") {
        // show dailyprogress + mdapprove
        if (key === "approve") return false;
        if (key === "dailyprogress" || key === "mdapprove") return true;
      } else if (pageName === "Daily Progress Entry") {
        // show mdapprove only (aspx shows last cell)
        if (key === "mdapprove") return true;
        if (key === "approve" || key === "dailyprogress") return false;
      } else {
        // default: hide approve/dailyprogress/mdapprove for other pages
        if (["approve", "dailyprogress", "mdapprove"].includes(key)) return false;
      }

      return true;
    }

    // Payroll grid rules (gv_RightsPay_RowDataBound)
    const subName = String(row.fld_submodulename || "");
    const pageId = String(row.fld_pageid || "");
    const pageName = String(row.fld_pagename || "");

    // submodule based hides
    if (subName === "Masters") {
      if (["print", "email", "sms"].includes(key)) return false;
      if (key === "depVisible") return false;
      if (key === "dailyAttendanceSpecial") return false;
    }
    if (subName === "Transactions") {
      if (["print", "email", "del", "sms"].includes(key)) return false;
      if (key === "depVisible") return false;
      if (key === "dailyAttendanceSpecial") return false;
    }
    if (subName === "Reports") {
      if (["add", "del", "print"].includes(key)) return false;
      if (key === "depVisible") return false;
      if (key === "dailyAttendanceSpecial") return false;
    }

    // Department visible only for certain pageIds in ASPX: 35,37,40,80
    if (key === "depVisible") {
      return ["35", "37", "40", "80"].includes(pageId);
    }

    // Daily attendance special checkbox shown only when page name = Daily Attendance
    if (key === "dailyAttendanceSpecial") {
      return pageName === "Daily Attendance";
    }

    return true;
  }

  // -----------------------------
  // Meta loading (usertypes, modules, submodules initial)
  // -----------------------------
  const loadUserTypes = async () => {
    const res = await axios.get(`${BASE_URL}/api/rights/usertypes`);
    if (!res.data?.success) throw new Error(res.data?.message || "Failed to load usertypes");
    setUserTypes(res.data.list || []);
  };

  const loadModules = async () => {
    const res = await axios.get(`${BASE_URL}/api/rights/modules`);
    if (!res.data?.success) throw new Error(res.data?.message || "Failed to load modules");
    setModules(res.data.list || []);
  };

  const loadSubmodules = async (moduleName) => {
    const headers = {};
    if (moduleName === "Payroll" && finYearId) headers["x-finyear-id"] = finYearId;

    const res = await axios.get(`${BASE_URL}/api/rights/submodules`, {
      params: { module: moduleName },
      headers,
    });

    if (!res.data?.success) throw new Error(res.data?.message || "Failed to load submodules");
    setSubmodules(res.data.list || []);
  };

  useEffect(() => {
    (async () => {
      try {
        setLoadingMeta(true);
        await Promise.all([loadUserTypes(), loadModules()]);
        await loadSubmodules("Select");
      } catch (e) {
        console.error(e);
        toast.error(e.message || "Failed to load initial data");
      } finally {
        setLoadingMeta(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -----------------------------
  // When module changes: reload submodules + refresh users if usertype selected
  // -----------------------------
  useEffect(() => {
    (async () => {
      try {
        setSelectedSubmoduleId("Select");
        setRows([]);
        await loadSubmodules(selectedModule);

        if (selectedUserTypeId !== "Select") {
          await fetchUsers(selectedUserTypeId, selectedModule);
        }
      } catch (e) {
        console.error(e);
        toast.error(e.message || "Failed to update module dependencies");
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModule]);

  // -----------------------------
  // Fetch users under selected user type
  // -----------------------------
  const fetchUsers = async (userTypeId, moduleName) => {
    const headers = {};
    if (moduleName === "Payroll") {
      if (!finYearId) {
        toast.error("finYearId missing in localStorage for Payroll module");
        return;
      }
      headers["x-finyear-id"] = finYearId;
    }

    const res = await axios.get(`${BASE_URL}/api/rights/users`, {
      params: { userTypeId, module: moduleName },
      headers,
    });

    if (!res.data?.success) {
      toast.error(res.data?.message || "Failed to load users");
      setUsers([]);
      return;
    }

    setUsers(res.data.list || []);
  };

  // When usertype changes
  useEffect(() => {
    (async () => {
      if (selectedUserTypeId === "Select") {
        setUsers([]);
        setSelectedUserId("Select");
        return;
      }
      try {
        await fetchUsers(selectedUserTypeId, selectedModule);
        setSelectedUserId("Select");
      } catch (e) {
        console.error(e);
        toast.error(e.message || "Failed to load users");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserTypeId]);

  // -----------------------------
  // Search validation (ASPX validate())
  // -----------------------------
  const validateSearch = () => {
    if (!mode) {
      toast.error("Please check user wise or user type wise");
      return false;
    }
    if (selectedUserTypeId === "Select") {
      toast.error("Please select usertype");
      return false;
    }
    if (mode === "user" && selectedUserId === "Select") {
      toast.error("Please select user name");
      return false;
    }
    return true;
  };

  // -----------------------------
  // Search pages
  // -----------------------------
  const handleSearch = async () => {
    if (!validateSearch()) return;

    try {
      setLoadingPages(true);

      const headers = {};
      if (isPayroll) {
        if (!finYearId) {
          toast.error("finYearId missing in localStorage for Payroll module");
          return;
        }
        headers["x-finyear-id"] = finYearId;
      }

      const userId = mode === "user" ? Number(selectedUserId) : 0; // ASPX uses userid only when selected; type-wise can show blank rights too
      const moduleId = !isPayroll && selectedSubmoduleId !== "Select" ? Number(selectedSubmoduleId) : 0;

      const res = await axios.get(`${BASE_URL}/api/rights/pages`, {
        params: { module: selectedModule, userId, moduleId },
        headers,
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load pages");
        return;
      }

      const list = res.data.list || [];

      // Map rows into checkbox state keys
      const mapped = list.map((r) => ({
        ...r,
        pageId: Number(r.fld_pageid),
        rights: !!Number(r.fld_rights || 0),
        add: !!Number(r.fld_addrights || 0),
        edit: !!Number(r.fld_editrights || 0),
        del: !!Number(r.fld_deleterights || 0),
        print: !!Number(r.fld_printrights || 0),
        email: !!Number(r.fld_emailrights || 0),
        sms: !!Number(r.fld_smsrights || 0),
        camp: !!Number(r.fld_camprights || 0),
        backdateentry: !!Number(r.fld_backdateentryrights || 0),
        approve: !!Number(r.fld_approverights || 0),
        mdapprove: !!Number(r.fld_mdapproverights || 0),
        dailyprogress: !!Number(r.fld_dailyprogressrights || 0),
        depVisible: !!Number(r.fld_DepVisiblerights || 0),
        dailyAttendanceSpecial: false, // UI-only special checkbox
      }));

      setRows(mapped);
      toast.success(`Loaded ${mapped.length} page(s)`);
    } catch (err) {
      console.error("rights search error:", err);
      toast.error(err.response?.data?.message || "Server error while loading pages");
    } finally {
      setLoadingPages(false);
    }
  };

  // -----------------------------
  // Toggle row checkbox
  // -----------------------------
  const toggleRow = (index, key, value) => {
    setRows((prev) => {
      const copy = [...prev];
      const row = { ...copy[index] };

      // Payroll: add ties edit in ASPX save logic, but we keep both for UI consistency
      if (isPayroll && key === "add") {
        row.add = value;
        row.edit = value;
      } else {
        row[key] = value;
      }

      copy[index] = row;
      return copy;
    });
  };

  // -----------------------------
  // Select all for a column (only visible rows)
  // -----------------------------
  const selectAllColumn = (key, checked) => {
    setRows((prev) =>
      prev.map((r) => {
        if (!isCheckboxVisible(r, key)) return r;
        if (isPayroll && key === "add") return { ...r, add: checked, edit: checked };
        return { ...r, [key]: checked };
      })
    );
  };

  const isAllChecked = (key) => {
    const visible = rows.filter((r) => isCheckboxVisible(r, key));
    if (visible.length === 0) return false;
    return visible.every((r) => {
      if (isPayroll && key === "add") return !!r.add;
      return !!r[key];
    });
  };

  // -----------------------------
  // Save (SET_RESET) logic
  // -----------------------------
  const handleSave = async () => {
    if (!validateSearch()) return;

    if (rows.length === 0) {
      toast.error("No pages loaded. Click Search first.");
      return;
    }

    try {
      setSaving(true);

      const headers = {};
      if (isPayroll) {
        if (!finYearId) {
          toast.error("finYearId missing in localStorage for Payroll module");
          return;
        }
        headers["x-finyear-id"] = finYearId;
      }

      const payload = {
        module: selectedModule,
        mode: mode === "usertype" ? "usertype" : "user",
        overwriteAll,
        userTypeId: Number(selectedUserTypeId),
        userId: mode === "user" ? Number(selectedUserId) : 0,
        moduleId: !isPayroll && selectedSubmoduleId !== "Select" ? Number(selectedSubmoduleId) : 0,
        rows: rows.map((r) => ({
          pageId: r.pageId,
          rights: !!r.rights,
          add: !!r.add,
          edit: !!r.edit,
          del: !!r.del,
          print: !!r.print,
          email: !!r.email,
          sms: !!r.sms,
          camp: !!r.camp,
          backdateentry: !!r.backdateentry,
          approve: !!r.approve,
          mdapprove: !!r.mdapprove,
          dailyprogress: !!r.dailyprogress,
          depVisible: !!r.depVisible,
        })),
      };

      const res = await axios.post(`${BASE_URL}/api/rights/save`, payload, { headers });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Save failed");
        return;
      }

      toast.success(res.data.message || "Saved successfully");
    } catch (err) {
      console.error("rights save error:", err);
      toast.error(err.response?.data?.message || "Server error while saving");
    } finally {
      setSaving(false);
    }
  };

  // -----------------------------
  // UI helpers
  // -----------------------------
  const userTypeOptions = userTypes.map((u) => ({ id: u.fld_usertypeid, name: u.fld_usertype }));
  const userOptions = users.map((u) => ({ id: u.fld_setupid, name: u.fld_username }));

  // Message row pinned to the visible part of the scroller (not the full table width)
  const renderMessageRow = (text, extraClass = "") => (
    <tr>
      <td colSpan={4 + columns.length} className="p-0">
        <div
          className={`sticky left-0 px-4 py-6 text-center ${extraClass}`}
          style={{ width: tableViewWidth || "100%" }}
        >
          {text}
        </div>
      </td>
    </tr>
  );

  return (
    <AdminWrapper>
      <div
        className={`min-h-screen w-full max-w-full min-w-0 overflow-x-hidden ${
          isDark ? "bg-gray-900" : "bg-gray-100"
        }`}
      >
        <HeaderBar
          title={<span className="font-dmserif text-3xl">Modules & Rights Management</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        {/* ✅ Center + limit width so page never overflows */}
        <div className="w-full max-w-7xl min-w-0 mx-auto px-4 md:px-6">
          <div
            className={`p-6 mt-6 rounded-2xl shadow-xl border min-w-0 ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
                : "bg-white text-black"
            }`}
          >
            {/* Header row */}
            <div className="flex justify-between items-center gap-3 flex-wrap mb-6">
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={handleBack}
                  disabled={saving}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    isDark
                      ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                  } disabled:opacity-60`}
                >
                  <ChevronLeft className="w-5 h-5" />
                  Back
                </button>
                <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Modules and Rights
                </h1>
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                  }`}
                >
                  {rows.length} Pages
                </span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => window.open("/admin/help/rights-management", "_blank")}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow hover:shadow-md transition ${
                    isDark
                      ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300"
                  }`}
                >
                  <HelpCircle className="w-5 h-5" />
                  Help
                </button>

                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    isDark
                      ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                  }`}
                >
                  {showFilters ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  {showFilters ? "Hide Filters" : "Show Filters"}
                </button>
              </div>
            </div>

            {/* Filters */}
            {showFilters && (
              <div
                className={`rounded-xl border mb-6 overflow-hidden ${
                  isDark ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <div className={`${isDark ? "bg-gray-800" : "bg-gray-50"} p-6`}>
                  <div className="grid grid-cols-12 gap-4">
                    {/* Mode */}
                    <div className="col-span-12 md:col-span-6">
                      <div className="flex items-center gap-4 flex-wrap">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="mode"
                            checked={mode === "usertype"}
                            onChange={() => {
                              setMode("usertype");
                              setSelectedUserId("Select");
                            }}
                          />
                          <span>User Type wise</span>
                        </label>

                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="mode"
                            checked={mode === "user"}
                            onChange={() => setMode("user")}
                          />
                          <span>User wise</span>
                        </label>

                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={overwriteAll}
                            onChange={(e) => setOverwriteAll(e.target.checked)}
                            disabled={mode !== "usertype"}
                          />
                          <span className={`${mode !== "usertype" ? "opacity-50" : ""}`}>
                            Overwrite rights to all users
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Module */}
                    <div className="col-span-12 md:col-span-3">
                      <label className="text-sm font-medium block mb-1">Module</label>
                      <select
                        value={selectedModule}
                        onChange={(e) => setSelectedModule(e.target.value)}
                        className={`w-full px-3 py-2 rounded-lg border ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white"
                            : "bg-white border-gray-300 text-black"
                        }`}
                      >
                        <option value="Select">Select</option>
                        {modules.map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Submodule */}
                    <div className="col-span-12 md:col-span-3">
                      <label className="text-sm font-medium block mb-1">SubModule</label>
                      <select
                        value={selectedSubmoduleId}
                        onChange={(e) => setSelectedSubmoduleId(e.target.value)}
                        disabled={isPayroll}
                        className={`w-full px-3 py-2 rounded-lg border ${
                          isPayroll ? "opacity-60 cursor-not-allowed" : ""
                        } ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white"
                            : "bg-white border-gray-300 text-black"
                        }`}
                      >
                        <option value="Select">Select</option>
                        {submodules.map((s) => (
                          <option key={s.fld_moduleid} value={s.fld_moduleid}>
                            {s.fld_moduledescription}
                          </option>
                        ))}
                      </select>
                      {isPayroll && (
                        <div className="text-xs mt-1 opacity-70">SubModule filter disabled for Payroll</div>
                      )}
                    </div>

                    {/* User Type */}
                    <div className="col-span-12 md:col-span-6">
                      <label className="text-sm font-medium block mb-1">
                        User Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={selectedUserTypeId}
                        onChange={(e) => setSelectedUserTypeId(e.target.value)}
                        className={`w-full px-3 py-2 rounded-lg border ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white"
                            : "bg-white border-gray-300 text-black"
                        }`}
                        disabled={loadingMeta}
                      >
                        <option value="Select">Select</option>
                        {userTypeOptions.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* User Name */}
                    <div className="col-span-12 md:col-span-6">
                      <label className="text-sm font-medium block mb-1">User Name</label>
                      <select
                        value={selectedUserId}
                        onChange={(e) => setSelectedUserId(e.target.value)}
                        disabled={mode !== "user"}
                        className={`w-full px-3 py-2 rounded-lg border ${
                          mode !== "user" ? "opacity-60 cursor-not-allowed" : ""
                        } ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white"
                            : "bg-white border-gray-300 text-black"
                        }`}
                      >
                        <option value="Select">Select</option>
                        {userOptions.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name}
                          </option>
                        ))}
                      </select>
                      {isPayroll && (
                        <div className="text-xs mt-1 opacity-70">
                          Payroll uses finYearId header (localStorage finYearId = {finYearId || "missing"})
                        </div>
                      )}
                    </div>

                    {/* Buttons */}
                    <div className="col-span-12 flex justify-end gap-3 mt-2">
                      <button
                        type="button"
                        onClick={handleSearch}
                        disabled={loadingPages}
                        className={`flex items-center gap-2 px-5 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                          isDark ? "bg-gray-700 hover:bg-gray-600 text-white" : `${buttonClass} ${hoverClass} text-white`
                        } disabled:opacity-60`}
                      >
                        <Search className="w-5 h-5" />
                        {loadingPages ? "Searching..." : "Search"}
                      </button>

                      <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving || rows.length === 0}
                        className={`flex items-center gap-2 px-5 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${`${buttonClass} ${hoverClass} text-white`} disabled:opacity-60`}
                      >
                        <Save className="w-5 h-5" />
                        {saving ? "Saving..." : "Set / Reset"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ✅ Table: only this area scrolls horizontally */}
            <div
              className={`rounded-xl shadow-sm border min-w-0 max-w-full overflow-hidden ${
                isDark ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <div
                ref={tableScrollRef}
                className="w-full max-w-full min-w-0 overflow-x-auto overflow-y-auto max-h-[70vh]"
              >
                <table className="w-max min-w-full border-collapse">
                  <thead className={`${buttonClass} text-white sticky top-0 z-10`}>
                    <tr>
                      <th className="px-3 py-3 text-center font-semibold border-r border-white/20 whitespace-nowrap">
                        Slno
                      </th>
                      <th className="px-3 py-3 text-left font-semibold border-r border-white/20 whitespace-nowrap">
                        Module Name
                      </th>
                      <th className="px-3 py-3 text-left font-semibold border-r border-white/20 whitespace-nowrap">
                        Sub Module Name
                      </th>
                      <th className="px-3 py-3 text-left font-semibold border-r border-white/20 whitespace-nowrap">
                        Page Name
                      </th>

                      {columns.map((c) => (
                        <th
                          key={c.key}
                          className="px-3 py-3 text-center font-semibold border-r border-white/20 whitespace-nowrap"
                        >
                          <div className="flex items-center justify-center gap-2">
                            <span>{c.label}</span>
                            {c.key !== "dailyAttendanceSpecial" && (
                              <input
                                type="checkbox"
                                checked={isAllChecked(c.key)}
                                onChange={(e) => selectAllColumn(c.key, e.target.checked)}
                                className="w-4 h-4 cursor-pointer accent-white"
                                title="Select all"
                              />
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {loadingPages
                      ? renderMessageRow("Loading...")
                      : rows.length === 0
                      ? renderMessageRow("No records found (search to load pages)", "opacity-70")
                      : rows.map((r, idx) => (
                          <tr
                            key={r.pageId || idx}
                            className={`transition-colors duration-200 ${
                              idx % 2 === 0
                                ? isDark
                                  ? "bg-gray-800"
                                  : "bg-gray-50"
                                : isDark
                                ? "bg-gray-900"
                                : "bg-white"
                            }`}
                          >
                            <td className="px-3 py-3 text-center text-sm border-r border-gray-200 dark:border-gray-700 whitespace-nowrap">
                              {idx + 1}
                            </td>
                            <td className="px-3 py-3 text-sm border-r border-gray-200 dark:border-gray-700 whitespace-nowrap">
                              {r.fld_moduledescription}
                            </td>
                            <td className="px-3 py-3 text-sm border-r border-gray-200 dark:border-gray-700 whitespace-nowrap">
                              {r.fld_submodulename}
                            </td>
                            <td className="px-3 py-3 text-sm border-r border-gray-200 dark:border-gray-700 font-medium whitespace-nowrap">
                              {r.fld_pagename}
                            </td>

                            {columns.map((c) => {
                              const visible = isCheckboxVisible(r, c.key);

                              if (!visible) {
                                return (
                                  <td key={c.key} className="px-3 py-3 text-center text-xs opacity-40 whitespace-nowrap">
                                    —
                                  </td>
                                );
                              }

                              if (c.key === "dailyAttendanceSpecial") {
                                return (
                                  <td key={c.key} className="px-3 py-3 text-center whitespace-nowrap">
                                    <input
                                      type="checkbox"
                                      checked={!!r.dailyAttendanceSpecial}
                                      onChange={(e) => toggleRow(idx, "dailyAttendanceSpecial", e.target.checked)}
                                      className={`w-4 h-4 cursor-pointer ${isDark ? "accent-blue-400" : "accent-blue-600"}`}
                                    />
                                  </td>
                                );
                              }

                              return (
                                <td key={c.key} className="px-3 py-3 text-center whitespace-nowrap">
                                  <input
                                    type="checkbox"
                                    checked={!!(isPayroll && c.key === "add" ? r.add : r[c.key])}
                                    onChange={(e) => toggleRow(idx, c.key, e.target.checked)}
                                    className={`w-4 h-4 cursor-pointer ${isDark ? "accent-blue-400" : "accent-blue-600"}`}
                                  />
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                  </tbody>
                </table>
              </div>
            </div>

            <ToastContainer
              position="top-right"
              autoClose={3000}
              hideProgressBar={false}
              closeOnClick
              pauseOnHover
              theme={isDark ? "dark" : "light"}
            />
          </div>
        </div>
      </div>
    </AdminWrapper>
  );
};

export default ModulesAndRightsManagement;